(() => {
  const storageKey = 'friendsBrowseContext';
  const historyKey = 'friendsBrowseContext';
  const activeCollectionKey = 'friendsActiveBrowseContext';
  const productStorageKey = 'friendsProductBrowseContext';
  const productHistoryKey = 'friendsProductBrowseContext';
  const maxContextAge = 5 * 60 * 1000;
  const maxActiveCollectionAge = 60 * 60 * 1000;

  function normalizePath(url) {
    try {
      const parsedUrl = new URL(url, window.location.href);
      const normalizedPath = parsedUrl.pathname.replace(/\/$/, '');
      return normalizedPath || '/';
    } catch (error) {
      return '';
    }
  }

  function rememberContext(anchor) {
    const context = anchor.dataset.browseContext;
    const menu = anchor.dataset.browseMenu;
    const destinationPath = normalizePath(anchor.href);

    if (!context || !/(^|\/)collections\//.test(destinationPath)) {
      return;
    }

    try {
      if (new URL(anchor.href, window.location.href).origin !== window.location.origin) {
        return;
      }
    } catch (error) {
      return;
    }

    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        context,
        menu,
        destinationPath,
        recordedAt: Date.now()
      }));
    } catch (error) {
      // Context tracking is an enhancement; navigation still works without storage.
    }
  }

  function readStorage(key, removeAfterReading = false, maxAge = maxContextAge) {
    try {
      const storedValue = JSON.parse(sessionStorage.getItem(key));

      if (removeAfterReading) {
        sessionStorage.removeItem(key);
      }

      if (!storedValue || Date.now() - storedValue.recordedAt > maxAge) {
        return null;
      }

      return storedValue;
    } catch (error) {
      try {
        if (removeAfterReading) {
          sessionStorage.removeItem(key);
        }
      } catch (storageError) {
        // Storage may be unavailable; the Liquid fallback remains usable.
      }

      return null;
    }
  }

  function consumeContext() {
    return readStorage(storageKey, true);
  }

  function readHistoryContext() {
    try {
      return window.history.state?.[historyKey] || null;
    } catch (error) {
      return null;
    }
  }

  function rememberHistoryContext(context) {
    try {
      window.history.replaceState({
        ...(window.history.state || {}),
        [historyKey]: context
      }, '');
    } catch (error) {
      // History state is optional; the current render has already been selected.
    }
  }

  function buildBreadcrumbItems(browseBy, selectedContext) {
    const contextParts = selectedContext.split('.');
    const breadcrumbItems = [];

    for (let depth = 1; depth <= contextParts.length; depth += 1) {
      const context = contextParts.slice(0, depth).join('.');
      const link = browseBy.querySelector(`a[data-browse-context="${context}"]`);

      if (!link) {
        continue;
      }

      try {
        const parsedUrl = new URL(link.href, window.location.href);

        if (parsedUrl.origin !== window.location.origin) {
          continue;
        }

        breadcrumbItems.push({
          context,
          title: link.textContent.trim(),
          url: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        });
      } catch (error) {
        // Skip malformed menu links while retaining the remaining path.
      }
    }

    return breadcrumbItems;
  }

  function rememberActiveCollectionContext(browseBy, selectedLink) {
    const breadcrumbItems = buildBreadcrumbItems(browseBy, selectedLink.dataset.browseContext);

    if (breadcrumbItems.length === 0) {
      return;
    }

    storeActiveCollectionContext({
      context: selectedLink.dataset.browseContext,
      menu: browseBy.dataset.browseMenu,
      sourcePath: normalizePath(window.location.href),
      breadcrumbItems
    });
  }

  function storeActiveCollectionContext(context) {
    try {
      sessionStorage.setItem(activeCollectionKey, JSON.stringify({
        ...context,
        recordedAt: Date.now()
      }));
    } catch (error) {
      // Product breadcrumbs fall back to Focal's Liquid breadcrumb.
    }
  }

  function rememberStandaloneCollectionContext() {
    if (document.querySelector('friends-browse-by')) {
      return;
    }

    const currentPath = normalizePath(window.location.href);
    const menuLinks = Array.from(document.querySelectorAll('a[data-browse-context][data-browse-menu]'));
    const currentRootLink = menuLinks.find((link) => {
      return /^\d+$/.test(link.dataset.browseContext)
        && normalizePath(link.href) === currentPath;
    });

    if (!currentRootLink || !/(^|\/)collections\//.test(currentPath)) {
      return;
    }

    try {
      const parsedUrl = new URL(currentRootLink.href, window.location.href);

      if (parsedUrl.origin !== window.location.origin) {
        return;
      }

      storeActiveCollectionContext({
        context: currentRootLink.dataset.browseContext,
        menu: currentRootLink.dataset.browseMenu,
        sourcePath: currentPath,
        breadcrumbItems: [{
          context: currentRootLink.dataset.browseContext,
          title: currentRootLink.textContent.trim(),
          url: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        }]
      });
    } catch (error) {
      // The Liquid breadcrumb remains available if the menu link is invalid.
    }
  }

  function rememberProductContext(anchor) {
    try {
      const parsedUrl = new URL(anchor.href, window.location.href);

      if (parsedUrl.origin !== window.location.origin || !/(^|\/)products\//.test(parsedUrl.pathname)) {
        return;
      }

      const productItem = anchor.closest('product-item');

      if (!productItem || !productItem.closest('[data-friends-collection-products]')) {
        sessionStorage.removeItem(productStorageKey);
        return;
      }

      const activeContext = readStorage(activeCollectionKey, false, maxActiveCollectionAge);

      if (!activeContext || activeContext.sourcePath !== normalizePath(window.location.href)) {
        return;
      }

      sessionStorage.setItem(productStorageKey, JSON.stringify({
        ...activeContext,
        destinationPath: normalizePath(parsedUrl.href),
        recordedAt: Date.now()
      }));
    } catch (error) {
      // Product navigation still works without contextual breadcrumbs.
    }
  }

  function readProductHistoryContext() {
    try {
      return window.history.state?.[productHistoryKey] || null;
    } catch (error) {
      return null;
    }
  }

  function rememberProductHistoryContext(context) {
    try {
      window.history.replaceState({
        ...(window.history.state || {}),
        [productHistoryKey]: context
      }, '');
    } catch (error) {
      // History state is optional; the contextual breadcrumb is already shown.
    }
  }

  document.addEventListener('click', (event) => {
    const anchor = event.target instanceof Element
      ? event.target.closest('a[href]')
      : null;

    if (anchor) {
      if (anchor.dataset.browseContext) {
        rememberContext(anchor);
      }

      rememberProductContext(anchor);
    }
  }, { capture: true });

  if (!window.customElements.get('friends-browse-by')) {
    class FriendsBrowseBy extends HTMLElement {
      connectedCallback() {
        this.selectContext();
      }

      selectContext() {
        const currentLinks = Array.from(this.querySelectorAll('[data-browse-current="true"]'));

        if (currentLinks.length === 0) {
          return;
        }

        const storedContext = consumeContext();
        const historyContext = readHistoryContext();
        const currentPath = normalizePath(window.location.href);
        const contextCandidates = [storedContext, historyContext].filter(Boolean);
        let selectedContextData = null;
        let contextualLink = null;

        for (const contextCandidate of contextCandidates) {
          if (contextCandidate.destinationPath !== currentPath || contextCandidate.menu !== this.dataset.browseMenu) {
            continue;
          }

          contextualLink = currentLinks.find((link) => link.dataset.browseContext === contextCandidate.context);

          if (contextualLink) {
            selectedContextData = contextCandidate;
            break;
          }
        }

        if (contextualLink && selectedContextData === storedContext) {
          rememberHistoryContext(storedContext);
        }

        const selectedLink = contextualLink || currentLinks[0];
        const selectedContext = selectedLink.dataset.browseContext;
        const selectedBranch = selectedLink.closest('[data-browse-branch]');

        this.querySelectorAll('[data-browse-branch]').forEach((branch) => {
          branch.hidden = branch !== selectedBranch;
        });

        this.querySelectorAll('[data-browse-subtree]').forEach((subtree) => {
          subtree.hidden = true;
        });

        const contextParts = selectedContext.split('.');

        selectedBranch.querySelectorAll('[data-browse-child]').forEach((child) => {
          child.hidden = contextParts.length >= 3
            && child.dataset.browseChild !== `${contextParts[0]}.${contextParts[1]}`;
        });

        if (contextParts.length >= 2) {
          const selectedSubtreeContext = `${contextParts[0]}.${contextParts[1]}`;
          const selectedSubtree = this.querySelector(`[data-browse-subtree="${selectedSubtreeContext}"]`);

          if (selectedSubtree) {
            selectedSubtree.hidden = false;
          }
        }

        rememberActiveCollectionContext(this, selectedLink);
      }
    }

    window.customElements.define('friends-browse-by', FriendsBrowseBy);
  }

  if (!window.customElements.get('friends-product-breadcrumb')) {
    class FriendsProductBreadcrumb extends HTMLElement {
      connectedCallback() {
        const storedContext = readStorage(productStorageKey, true);
        const historyContext = readProductHistoryContext();
        const currentPath = normalizePath(window.location.href);
        const selectedContext = [storedContext, historyContext].find((context) => {
          return context
            && context.destinationPath === currentPath
            && Array.isArray(context.breadcrumbItems)
            && context.breadcrumbItems.length > 0;
        });

        if (!selectedContext) {
          return;
        }

        if (selectedContext === storedContext) {
          rememberProductHistoryContext(selectedContext);
        }

        this.renderBreadcrumb(selectedContext.breadcrumbItems, selectedContext.menu);
      }

      renderBreadcrumb(breadcrumbItems, menu) {
        const list = this.querySelector('[data-friends-breadcrumb-list]');
        const homeItem = list?.firstElementChild?.cloneNode(true);
        const productItem = list?.lastElementChild?.cloneNode(true);

        if (!list || !homeItem || !productItem) {
          return;
        }

        const safeItems = breadcrumbItems.reduce((items, item) => {
          if (
            !item
            || typeof item.context !== 'string'
            || !/^\d+(\.\d+){0,2}$/.test(item.context)
            || typeof item.title !== 'string'
            || typeof item.url !== 'string'
          ) {
            return items;
          }

          try {
            const parsedUrl = new URL(item.url, window.location.href);

            if (parsedUrl.origin !== window.location.origin || !/(^|\/)collections\//.test(parsedUrl.pathname)) {
              return items;
            }

            items.push({
              context: item.context,
              title: item.title.trim(),
              url: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
            });
          } catch (error) {
            // Ignore invalid stored breadcrumb items.
          }

          return items;
        }, []);

        if (safeItems.length === 0) {
          return;
        }

        const collectionItems = safeItems.map((item) => {
          const listItem = document.createElement('li');
          const link = document.createElement('a');

          listItem.className = 'breadcrumb__item';
          link.className = 'breadcrumb__link';
          link.href = item.url;
          link.textContent = item.title;
          link.dataset.browseContext = item.context;

          if (typeof menu === 'string' && menu) {
            link.dataset.browseMenu = menu;
          }

          listItem.append(link);

          return listItem;
        });

        list.replaceChildren(homeItem, ...collectionItems, productItem);
      }
    }

    window.customElements.define('friends-product-breadcrumb', FriendsProductBreadcrumb);
  }

  rememberStandaloneCollectionContext();
})();
